import { useEffect, useRef, useState, useCallback } from "react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";
import { api, ALLOWED_REACTIONS } from "@/lib/api";
import { Send, Loader2, Megaphone, Trash2, Crown, Skull, Lock } from "lucide-react";
import { RoleBadge } from "@/components/RoleBadge";
import { RankBadge } from "@/components/RankBadge";
import { StreakBadge } from "@/components/StreakBadge";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { playPopSound } from "@/lib/sound";
import { toast } from "sonner";
import { HelpRequestButton } from "@/components/HelpRequestButton";
import { LoungeChat } from "@/components/LoungeChat";

function relativeTime(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 5) return "just now";
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
}

const MessageBubble = ({ m, currentUser, onReact, onDelete }) => {
    const reactions = m.reactions || {};
    const canDelete = currentUser && (currentUser.role === "admin" || currentUser.role === "support");
    const accent = m.author_role === "admin" ? "#facc15" : m.author_role === "support" ? "#22d3ee" : "#a855f7";
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="flex gap-3 group"
            data-testid="announcement-message"
        >
            <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm text-white" style={{ background: accent }}>
                {(m.author_name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <Link to={m.author_id ? `/u/${m.author_id}` : "#"} className="font-semibold text-white truncate hover:underline" data-testid="announcement-author-link">{m.author_name || "Owner"}</Link>
                    <RoleBadge role={m.author_role} />
                    {m.author_rank && <RankBadge rank={{ key: m.author_rank, streak: m.author_streak }} size="sm" showLabel={false} />}
                    {m.author_streak ? <StreakBadge streak={m.author_streak} size="sm" /> : null}
                    <span className="text-xs text-[#8b8ba0] font-mono">{relativeTime(m.created_at)}</span>
                </div>
                <div className="mt-1 rounded-lg pp-bg-2 border border-[#242436] px-3 py-2 inline-block max-w-full break-words" style={{ boxShadow: `0 0 0 1px ${accent}22` }}>
                    <p className="text-[#e6e6f0] whitespace-pre-wrap">{m.content}</p>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                    {Object.entries(reactions).map(([emoji, ids]) => (
                        <button
                            key={emoji}
                            onClick={() => onReact(m.id, emoji)}
                            className={`px-2 py-0.5 rounded-full border text-xs flex items-center gap-1 ${ids.includes(currentUser?.id) ? "border-[#a855f7]/60 bg-[#1a0f2e]" : "border-[#242436] bg-[#14141c]"} hover:bg-[#1b1b26]`}
                            data-testid="announcement-reaction"
                        >
                            <span>{emoji}</span> <span className="text-[#b7b7c6]">{ids.length}</span>
                        </button>
                    ))}
                    {currentUser && (
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                            {ALLOWED_REACTIONS.filter((e) => !reactions[e]).slice(0, 5).map((e) => (
                                <button key={e} onClick={() => onReact(m.id, e)} className="px-1.5 py-0.5 rounded text-xs hover:bg-[#1b1b26]" data-testid="announcement-reaction-add">
                                    {e}
                                </button>
                            ))}
                        </div>
                    )}
                    {canDelete && (
                        <button onClick={() => onDelete(m.id)} className="opacity-0 group-hover:opacity-100 text-[#8b8ba0] hover:text-[#ef4444] ml-2" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default function Chat() {
    const { user, loading: authLoading } = useAuth();
    const [messages, setMessages] = useState([]);
    const [typing, setTyping] = useState([]);
    const [content, setContent] = useState("");
    const [sending, setSending] = useState(false);
    const lastIsoRef = useRef(null);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    const canPost = user && (user.role === "admin" || user.role === "support");

    const fetchMessages = useCallback(async (full = false) => {
        try {
            const params = {};
            if (!full && lastIsoRef.current) params.since = lastIsoRef.current;
            const res = await api.get("/announcements", { params });
            const items = res.data.items || [];
            setTyping((res.data.typing || []).filter((t) => t.user_id !== user?.id));
            if (items.length) {
                setMessages((cur) => {
                    const ids = new Set(cur.map((m) => m.id));
                    const merged = [...cur, ...items.filter((i) => !ids.has(i.id))];
                    merged.sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
                    return merged;
                });
                lastIsoRef.current = items[items.length - 1].created_at;
                if (!full) {
                    requestAnimationFrame(() => {
                        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                    });
                }
            } else if (full) {
                lastIsoRef.current = null;
            }
        } catch (e) { /* noop */ }
    }, [user]);

    // Refresh full list (and reactions) every 6s; new messages every 3s via since
    useEffect(() => {
        fetchMessages(true).then(() => {
            requestAnimationFrame(() => {
                if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            });
        });
    }, [fetchMessages]);

    useEffect(() => {
        const id1 = setInterval(() => fetchMessages(false), 3500);
        const id2 = setInterval(async () => {
            // Periodic full refresh to pull updated reactions
            try {
                const res = await api.get("/announcements", { params: { limit: 50 } });
                setMessages(res.data.items || []);
                setTyping((res.data.typing || []).filter((t) => t.user_id !== user?.id));
                if (res.data.items && res.data.items.length) {
                    lastIsoRef.current = res.data.items[res.data.items.length - 1].created_at;
                }
            } catch (e) { /* noop */ }
        }, 12000);
        return () => { clearInterval(id1); clearInterval(id2); };
    }, [fetchMessages, user]);

    const sendMessage = async (e) => {
        e?.preventDefault();
        if (!canPost) return;
        const c = content.trim();
        if (!c) return;
        setSending(true);
        try {
            await api.post("/announcements", { content: c });
            playPopSound();
            setContent("");
            await fetchMessages(false);
        } catch (err) {
            toast.error("Failed to send");
        } finally {
            setSending(false);
        }
    };

    const onTyping = async () => {
        if (!canPost) return;
        try { await api.post("/announcements/typing"); } catch (e) {}
    };

    const reactTo = async (id, emoji) => {
        if (!user) return toast.message("Log in to react");
        try {
            await api.post(`/announcements/${id}/react`, { emoji });
            await fetchMessages(false);
        } catch (e) { /* noop */ }
    };

    const deleteMsg = async (id) => {
        try { await api.delete(`/announcements/${id}`); setMessages((cur) => cur.filter((m) => m.id !== id)); } catch (e) {}
    };

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-8 sm:py-12">
                <div className="max-w-3xl mx-auto px-4">
                    <div className="flex items-end justify-between gap-4 mb-5">
                        <div>
                            <p className="text-xs font-mono uppercase text-[#a855f7] tracking-widest">Community</p>
                            <h1 className="font-display text-3xl sm:text-4xl tracking-widest inline-flex items-center gap-2"><Megaphone className="w-6 h-6 text-[#22d3ee]" /> CHAT</h1>
                            <p className="mt-1 text-sm text-[#8b8ba0]">Official announcements + dedicated lounges for Pro and Premium members.</p>
                        </div>
                    </div>

                    <Tabs defaultValue="announcements" className="w-full">
                        <TabsList className="pp-bg-1 border border-[#242436] mb-4 grid grid-cols-3 w-full">
                            <TabsTrigger value="announcements" className="data-[state=active]:bg-[#1b1b26] data-[state=active]:text-white" data-testid="chat-tab-announcements">
                                <Megaphone className="w-3.5 h-3.5 mr-1" /> Announcements
                            </TabsTrigger>
                            <TabsTrigger value="pro" className="data-[state=active]:bg-[#1b1b26] data-[state=active]:text-white" data-testid="chat-tab-pro">
                                <Crown className="w-3.5 h-3.5 mr-1 text-[#a855f7]" /> Pro Lounge
                            </TabsTrigger>
                            <TabsTrigger value="premium" className="data-[state=active]:bg-[#1b1b26] data-[state=active]:text-white" data-testid="chat-tab-premium">
                                <Skull className="w-3.5 h-3.5 mr-1 text-[#ef4444]" /> Premium Lounge
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="announcements">
                            <Card className="pp-bg-1 border-[#242436] text-white" data-testid="announcements-chat">
                        <CardContent className="p-0 flex flex-col h-[60vh] sm:h-[68vh]">
                            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="announcements-list">
                                <AnimatePresence>
                                    {messages.length === 0 ? (
                                        <p className="text-center text-[#8b8ba0] text-sm py-12">No announcements yet. Stay tuned for the first drop.</p>
                                    ) : messages.map((m) => (
                                        <MessageBubble key={m.id} m={m} currentUser={user} onReact={reactTo} onDelete={deleteMsg} />
                                    ))}
                                </AnimatePresence>
                            </div>
                            {typing.length > 0 && (
                                <div className="px-4 py-1 text-xs font-mono text-[#22d3ee]" data-testid="announcements-typing">
                                    {typing.map((t) => t.display_name).join(", ")} is typing…
                                </div>
                            )}
                            <div className="border-t border-[#242436] p-3">
                                {(() => {
                                    const tokenInStorage = typeof window !== "undefined" && localStorage.getItem("token");
                                    if (authLoading || (!user && tokenInStorage)) {
                                        return (
                                            <div className="text-center text-sm text-[#8b8ba0] py-2 inline-flex items-center justify-center gap-2 w-full" data-testid="announcements-auth-loading">
                                                <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                                            </div>
                                        );
                                    }
                                    if (canPost) {
                                        return (
                                            <form onSubmit={sendMessage} className="flex items-center gap-2" data-testid="announcement-compose">
                                                <Input ref={inputRef} value={content} onChange={(e) => { setContent(e.target.value); onTyping(); }} placeholder="Drop an announcement…" className="pp-bg-0 border-[#242436] text-white" data-testid="announcement-input" />
                                                <Button type="submit" disabled={sending || !content.trim()} className="bg-[#6f3fec] hover:brightness-110 text-white" data-testid="announcement-send-button">
                                                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                                </Button>
                                            </form>
                                        );
                                    }
                                    return (
                                        <div className="text-center text-sm text-[#8b8ba0] py-2" data-testid="announcements-readonly">
                                            Read-only. Only Owner & Support can post here.
                                        </div>
                                    );
                                })()}
                            </div>
                        </CardContent>
                    </Card>
                        </TabsContent>

                        <TabsContent value="pro">
                            <LoungeChat channel="pro" />
                        </TabsContent>

                        <TabsContent value="premium">
                            <LoungeChat channel="premium" />
                        </TabsContent>
                    </Tabs>
                </div>
            </section>
            <Footer />
            <HelpRequestButton />
        </div>
    );
}
